import AppContext, { IAppContext } from 'App/AppContext';
import { IPCheckbox, IPSeparator } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  FilterAnnouncementGroupKeys,
  FilterAnnouncementNeedsReviewValue,
} from 'components/Announcements/announcements';
import { FilterAssetGroupKeys } from 'components/Asset/FilterAssetGroupKeys';
import { getInquiryProgressAttentionFilterValue } from 'components/InquiryProgress/FilterInquiryProgressGroupKeys';
import { FilterProcessingActivityGroupKeys } from 'components/ProcessingActivity/FilterProcessingActivityGroupKeys';
import { getInquirySendStatusFilterValue } from 'components/ProjectInquiry/FilterInquirySendGroupKeys';
import { FilterRiskGroupKeys } from 'components/Risk/FilterRiskGroupKeys';
import { FilterTaskGroupKeys } from 'components/Tasks/Filter/FilterTaskGroupKeys';
import { FilterTenantGroupKeys, FilterTenantHideContainersValue } from 'components/Tenant/FilterTenantGroupKeys';
import { AnnouncementStatus } from 'models/announcement';
import { AssetState } from 'models/asset/asset';
import { ProcessingActivityRole, ProcessingActivityState } from 'models/processingActivity';
import { RiskStates } from 'models/riskState';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { TenantStatus } from 'utils/subscription';

const defQuickFilterCount = 5;

export interface IQuickFilterItemRenderer {
  filter: string;
}

export enum QuickFilterContext {
  allTasks = 1,
  myTasks = 2,
  myRequest = 3,
  scheduleTasks = 4,
  risks = 5,
  assets = 6,
  kpis = 8,
  processes = 9,
  objectives = 10,
  controls = 11,
  themes = 12,
  standards = 13,
  taskContext = 14,
  forms = 15,
  processingActivities = 16,
  library = 17,
  publication = 18,
  formFields = 19,
  tenants = 20,
  announcements = 21,
  assetInquiry = 23,
  outcomeInquiry = 24,
  inquiryProgress = 25,
  generic = 99,
}

interface ISavedQuickFilter {
  filter: string;
  usedCount: number;
}

const getQuickFilterStorageName = (context: QuickFilterContext): LocalStorageKeys => {
  switch (context) {
    case QuickFilterContext.allTasks:
      return LocalStorageKeys.QuickFilterAllTasks;
    case QuickFilterContext.myRequest:
      return LocalStorageKeys.QuickFilterMyRequests;
    case QuickFilterContext.myTasks:
      return LocalStorageKeys.QuickFilterMyTasks;
    case QuickFilterContext.scheduleTasks:
      return LocalStorageKeys.QuickFilterMonitoringTasks;
    case QuickFilterContext.taskContext:
      return LocalStorageKeys.QuickFilterTaskContext;
    case QuickFilterContext.risks:
      return LocalStorageKeys.QuickFilterRisks;
    case QuickFilterContext.assets:
      return LocalStorageKeys.QuickFilterAssets;
    case QuickFilterContext.kpis:
      return LocalStorageKeys.QuickFilterKPIs;
    case QuickFilterContext.forms:
      return LocalStorageKeys.QuickFilterForms;
    case QuickFilterContext.formFields:
      return LocalStorageKeys.QuickFilterFormFields;
    case QuickFilterContext.objectives:
      return LocalStorageKeys.QuickFilterObjectives;
    case QuickFilterContext.processes:
      return LocalStorageKeys.QuickFilterProcesses;
    case QuickFilterContext.controls:
      return LocalStorageKeys.QuickFilterControls;
    case QuickFilterContext.themes:
      return LocalStorageKeys.QuickFilterThemes;
    case QuickFilterContext.standards:
      return LocalStorageKeys.QuickFilterStandards;
    case QuickFilterContext.processingActivities:
      return LocalStorageKeys.QuickFilterProcessingActivities;
    case QuickFilterContext.generic:
      return LocalStorageKeys.QuickFilterGeneric;
    case QuickFilterContext.library:
      return LocalStorageKeys.QuickFilterLibrary;
    case QuickFilterContext.publication:
      return LocalStorageKeys.QuickFilterPublication;
    case QuickFilterContext.tenants:
      return LocalStorageKeys.QuickFilterTenants;
    case QuickFilterContext.announcements:
      return LocalStorageKeys.QuickFilterAnnouncements;
    case QuickFilterContext.assetInquiry:
      return LocalStorageKeys.QuickFilterAssetInquiry;
    case QuickFilterContext.outcomeInquiry:
      return LocalStorageKeys.QuickFilterOutcomeInquiry;
    case QuickFilterContext.inquiryProgress:
      return LocalStorageKeys.QuickFilterInquiryProgress;
  }
};

export const saveQuickFilter = (
  filters: ISavedQuickFilter[],
  context: QuickFilterContext,
  appContext: IAppContext | undefined,
) => {
  const storageKeyName = getQuickFilterStorageName(context);
  const newSavedQuickFilter = encodeQuickFilter(filters);
  setLocalStorageData(appContext, storageKeyName, newSavedQuickFilter);
};

export const loadQuickFilter = (
  context: QuickFilterContext,
  appContext: IAppContext | undefined,
): ISavedQuickFilter[] => {
  const storageKeyName = getQuickFilterStorageName(context);
  const savedQuickFilter = getLocalStorageData(appContext, storageKeyName);
  const quickFilter = decodeSavedQuickFilter(savedQuickFilter || '');

  return quickFilter;
};

export const clearQuickFilter = (context: QuickFilterContext, appContext: IAppContext | undefined) => {
  const storageKeyName = getQuickFilterStorageName(context);
  setLocalStorageData(appContext, storageKeyName, '');
};

const encodeQuickFilter = (filters: ISavedQuickFilter[]): string => {
  let encodedFilters: string = '';
  for (let idx = 0; idx < filters.length; idx++) {
    const filter = filters[idx];
    encodedFilters = encodedFilters + '@' + filter.filter + '#' + filter.usedCount.toString();
  }

  return encodedFilters.slice(1);
};

const decodeSavedQuickFilter = (encodedFilter: string): ISavedQuickFilter[] => {
  if (encodedFilter.length === 0) return [];
  const combinedfilters = encodedFilter.split('@');

  const filters: ISavedQuickFilter[] = [];
  for (let idx = 0; idx < combinedfilters.length; idx++) {
    const combinedfilter = combinedfilters[idx];
    const newFilter: ISavedQuickFilter = {
      filter: combinedfilter.split('#')[0],
      usedCount: Number(combinedfilter.split('#')[1]),
    };
    filters.push(newFilter);
  }

  return filters;
};

const removeQuickFilter = (filters: ISavedQuickFilter[], filter: string): ISavedQuickFilter[] => {
  return filters.filter((f) => f.filter !== filter);
};

const getNewQuickFilter = (filter: string): ISavedQuickFilter => {
  return {
    filter: filter,
    usedCount: 1,
  };
};

export const applyNewQuickFilters = (
  currentQuickFilters: ISavedQuickFilter[],
  newFilters: string[],
  maxFilters: number,
): ISavedQuickFilter[] => {
  //the current quick filters are from the current filters stored in a storageKey
  //the new filters are applied so that the new quick filters contain the 5 last used filters
  //rules:
  //1. when a new filter is not present in the current quick filters: add it to the back when count < max
  //2. when a new filter is not present in the current quick filters: when count = max replace the least used one starting from the end

  let newQuickFilters: ISavedQuickFilter[] = [...currentQuickFilters];

  for (let idx = 0; idx < newFilters.length; idx++) {
    const newFilter = newFilters[idx];
    const existingFilter = currentQuickFilters.find((f) => f.filter === newFilter);
    if (!existingFilter) {
      if (newQuickFilters.length < maxFilters) {
        newQuickFilters.push(getNewQuickFilter(newFilter));
      } else {
        const leastUsedCount = Math.min(...newQuickFilters.map((q) => q.usedCount));
        const leastUsedIdx: number = newQuickFilters.reverse().findIndex((f) => f.usedCount === leastUsedCount);
        newQuickFilters[newQuickFilters.length - leastUsedIdx - 1] = getNewQuickFilter(newFilter);
      }
    } else {
      existingFilter.usedCount += 1;
    }
  }

  if (newQuickFilters.length > maxFilters) {
    newQuickFilters = newQuickFilters.slice(0, maxFilters);
  }

  return newQuickFilters;
};

//
// Quick filter item
//
interface IQuickFilterItemProps {
  filter: string;
  checked: boolean;
  isLocked?: boolean;
  onCheck: (quickFilter: string, checked: boolean) => void;
  context: QuickFilterContext;
  itemRender: (props: IQuickFilterItemRenderer) => JSX.Element | null;
  onRemoveItem: (context: QuickFilterContext, filter: string) => void;
}

const QuickFilterItem = (props: IQuickFilterItemProps) => {
  //Get the JSX to render as filter item contents
  //When this returns null, the filter cannot be rendered due to any reason (e.g. label / user is deleted)
  //In that case, signal to the QuickFilter that this filter must be deleted
  const quickFilterItemRenderResult = props.itemRender({ filter: props.filter });

  if (!quickFilterItemRenderResult) {
    props.onRemoveItem(props.context, props.filter);

    return null;
  }

  return (
    <QuickFilterCheckbox
      checked={props.checked}
      disabled={props.isLocked}
      onChange={(checked) => props.onCheck(props.filter, checked)}
    >
      {quickFilterItemRenderResult}
    </QuickFilterCheckbox>
  );
};

interface IQuickFilterCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  /** The label; a click on it toggles the checkbox too. */
  children: ReactNode;
}

/**
 * One quick filter as the lists show it: a separator, a checkbox and its label. For a fixed filter that is not one of
 * the saved quick filters, such as Nog open and Aandacht nodig in the Uitvraag uitkomsten.
 */
export const QuickFilterCheckbox = (props: IQuickFilterCheckboxProps) => {
  return (
    <IPStack horizontal verticalAlign="center">
      <IPStack.Item styles={{ root: { paddingLeft: 5, paddingRight: 5 } }}>
        <IPSeparator vertical></IPSeparator>
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={props.checked}
          disabled={props.disabled}
          onChange={(_ev, checked) => {
            if (props.disabled) return;
            props.onChange(checked !== undefined ? checked : false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item
        styles={{
          root: {
            selectors: {
              '&:hover': {
                cursor: props.disabled ? 'default' : 'pointer',
              },
            },
          },
        }}
        onClick={() => {
          if (props.disabled) return;
          props.onChange(!props.checked);
        }}
      >
        {props.children}
      </IPStack.Item>
    </IPStack>
  );
};

//
// Quick filter
//
interface IQuickFilterProps {
  context: QuickFilterContext;
  filters: string[];
  onChange: (quickFilter: string, checked: boolean) => void;
  onFilterRender: (props: IQuickFilterItemRenderer) => JSX.Element | null;
  enabled: boolean;
  maxFilters?: number;
  lockedFilterValues?: string[];
  /**
   * A chip to leave out for now, such as one that would show no rows ("Aandacht nodig (0)"). It stays saved and comes back
   * once it matters again; a checked chip always shows, so it can be cleared.
   */
  isFilterHidden?: (filter: string) => boolean;
}

const QuickFilter = (props: IQuickFilterProps) => {
  const appContext = useContext(AppContext);
  const [quickFilters, setQuickFilters] = useState<ISavedQuickFilter[]>([]);

  const removeItem = (context: QuickFilterContext, filter: string) => {
    const currentQuickFilters = loadQuickFilter(context, appContext);
    const newQuickFilter = removeQuickFilter(currentQuickFilters, filter);
    saveQuickFilter(newQuickFilter, props.context, appContext);
  };

  useEffect(() => {
    const getDefaultFiltersForContext = (): ISavedQuickFilter[] => {
      switch (props.context) {
        case QuickFilterContext.allTasks:
          return [
            getNewQuickFilter(FilterTaskGroupKeys.assignment + 'notassigned'),
            getNewQuickFilter(FilterTaskGroupKeys.status + 'open'),
            getNewQuickFilter(FilterTaskGroupKeys.deadline + 'deadlineTooLate'),
          ];
        case QuickFilterContext.myRequest:
        case QuickFilterContext.myTasks:
          return [
            getNewQuickFilter(FilterTaskGroupKeys.startDate + 'startToday'),
            getNewQuickFilter(FilterTaskGroupKeys.deadline + 'deadlineTooLate'),
          ];
        case QuickFilterContext.scheduleTasks:
          return [getNewQuickFilter(FilterTaskGroupKeys.assignment + 'notassigned')];
        case QuickFilterContext.risks:
          return [getNewQuickFilter(FilterRiskGroupKeys.status + RiskStates.NotAccepted)];
        case QuickFilterContext.assets:
          return [getNewQuickFilter(FilterAssetGroupKeys.state + AssetState.Active)];
        case QuickFilterContext.taskContext:
          return [getNewQuickFilter(FilterTaskGroupKeys.status + 'open')];
        case QuickFilterContext.processingActivities:
          return [
            getNewQuickFilter(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Approved),
            getNewQuickFilter(FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Controller),
            getNewQuickFilter(FilterProcessingActivityGroupKeys.role + ProcessingActivityRole.Processor),
          ];
        case QuickFilterContext.tenants:
          return [
            getNewQuickFilter(FilterTenantGroupKeys.subscriptionType + FilterTenantHideContainersValue),
            getNewQuickFilter(FilterTenantGroupKeys.trial + TenantStatus.ActiveCustomer),
          ];
        case QuickFilterContext.announcements:
          return [
            getNewQuickFilter(FilterAnnouncementGroupKeys.status + AnnouncementStatus.Live),
            getNewQuickFilter(FilterAnnouncementGroupKeys.status + AnnouncementStatus.Scheduled),
            getNewQuickFilter(FilterAnnouncementGroupKeys.status + AnnouncementStatus.Draft),
            getNewQuickFilter(FilterAnnouncementGroupKeys.review + FilterAnnouncementNeedsReviewValue),
          ];
        // The send rooms: lateness is followed in Voortgang verantwoordelijken, so only Geen verantwoordelijke.
        case QuickFilterContext.assetInquiry:
        case QuickFilterContext.outcomeInquiry:
          return [getNewQuickFilter(getInquirySendStatusFilterValue('noResponsible'))];
        // Voortgang verantwoordelijken: only Te laat, off; the other attention points are in the
        // Filter callout.
        case QuickFilterContext.inquiryProgress:
          return [getNewQuickFilter(getInquiryProgressAttentionFilterValue('late'))];
        default:
          return [];
      }
    };

    let filters = loadQuickFilter(props.context, appContext);

    if (filters.length === 0) {
      filters = getDefaultFiltersForContext();
    }

    const newQuickFilter = applyNewQuickFilters(filters, props.filters, props.maxFilters ?? defQuickFilterCount);
    saveQuickFilter(newQuickFilter, props.context, appContext);

    setQuickFilters(newQuickFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.context, props.filters, props.maxFilters]);

  //
  // Main render
  //
  // A hidden chip shows only while checked; without any chip to show, nothing is rendered (no empty gap in the toolbar).
  const isVisible = (filter: string): boolean =>
    props.filters.includes(filter) || props.isFilterHidden?.(filter) !== true;
  if (!props.enabled || !quickFilters.some((quickFilter) => isVisible(quickFilter.filter))) return null;

  return (
    <IPStack horizontal wrap verticalAlign="center">
      {quickFilters.map((quickFilter) => {
        if (!isVisible(quickFilter.filter)) return null;
        const checked = props.filters.includes(quickFilter.filter);

        return (
          <IPStack.Item key={quickFilter.filter}>
            <QuickFilterItem
              filter={quickFilter.filter}
              onCheck={props.onChange}
              checked={checked}
              isLocked={props.lockedFilterValues?.includes(quickFilter.filter)}
              context={props.context}
              itemRender={props.onFilterRender}
              onRemoveItem={removeItem}
            />
          </IPStack.Item>
        );
      })}
    </IPStack>
  );
};
export default QuickFilter;
